import PropTypes from "prop-types";
import React, { useContext } from "react";
import { DialogTitle, DialogContent, DialogContentText, CircularProgress } from "@material-ui/core";
import styled from "styled-components";
import CheckIcon from "@material-ui/icons/Check";
import ClearIcon from "@material-ui/icons/Clear";
import { useTranslation } from "react-i18next";
import { GeofencePackageContext } from "../../context";

const STATUS_COLORS = {
  DONE: "green",
  FAILURE: "red",
  OTHER: "transparent",
};

const StyledDialogContent = styled(DialogContent)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  position: relative;
`;

const CloseIcon = styled(ClearIcon)`
  position: absolute;
  top: 24px;
  right: 24px;
  cursor: pointer;
`;

const LoadingIconWrap = styled.div`
  position: relative;
  width: 68px;
  height: 68px;
  > * {
    background: ${(props) => STATUS_COLORS[props.status || "OTHER"]};
    border-radius: 50%;
    transition: opacity 0.2s;
  }
`;

const Spinner = styled(CircularProgress)`
  > svg {
    opacity: ${(props) =>
      !props.status || !["FAILURE", "DONE"].includes(props.status) ? "1" : "0"};
  }
`;

const SuccessFailIcon = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  opacity: ${(props) => (props.status === "IN_PROGRESS" ? "0" : "1")};
  color: white;

  > * {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

const LoadingIcon = (props) => (
  <LoadingIconWrap {...props}>
    <Spinner size={68} color="inherit" {...props} />
    <SuccessFailIcon {...props}>
      {props.status === "DONE" && <CheckIcon color="primary" />}
      {props.status === "FAILURE" && <ClearIcon color="primary" />}
    </SuccessFailIcon>
  </LoadingIconWrap>
);

LoadingIcon.propTypes = {
  status: PropTypes.string.isRequired,
};

const parseFailureMessge = (message) => {
  let data;
  try {
    data = JSON.parse(message);
  } catch (err) {
    return message;
  }

  if (Array.isArray(data)) {
    return data.map((str) => <p>{str}</p>);
  }

  return message;
};

const GeofencePackageProgress = ({ handleClose }) => {
  const { currentStep, packageStatus, allSteps, packageSize } = useContext(GeofencePackageContext);
  const { t } = useTranslation();

  let statusMessage;

  switch (currentStep) {
    case allSteps.IN_PROGRESS:
      statusMessage = t("Fleet.Geofences.ExportModal.GeofenceBinaryModal.CreatingBinaries");
      break;
    case allSteps.PACKAGE_COMPLETE:
      statusMessage = t("Fleet.Geofences.ExportModal.GeofenceBinaryModal.PackageComplete");
      break;
    case allSteps.FAILURE:
      statusMessage = t("Fleet.Geofences.ExportModal.GeofenceBinaryModal.Failed");
      break;
    case allSteps.DOWNLOADING_GEOJSON:
      statusMessage = t("Fleet.Geofences.ExportModal.GeofenceBinaryModal.SavingGeoJSON");
      break;

    case allSteps.GEOJSON_DOWNLOAD_COMPLETE:
      statusMessage = t("Fleet.Geofences.ExportModal.GeofenceBinaryModal.GeoJSONSaved");
      break;
    case allSteps.DOWNLOADING_RULECHAIN:
      statusMessage = t("Fleet.Geofences.ExportModal.GeofenceBinaryModal.SavingRuleChain");
      break;
    case allSteps.RULECHAIN_DOWNLOAD_COMPLETE:
      statusMessage = t("Fleet.Geofences.ExportModal.GeofenceBinaryModal.RulechainFileSaved");
      break;
    case allSteps.DONE:
      statusMessage = t("Fleet.Geofences.ExportModal.GeofenceBinaryModal.Done");
      break;
    default:
      statusMessage = t("Fleet.Geofences.ExportModal.GeofenceBinaryModal.WarmingUp");
  }

  return (
    <StyledDialogContent>
      <CloseIcon onClick={handleClose} />
      <DialogContentText component="div">
        <LoadingIcon status={currentStep} />
      </DialogContentText>
      <DialogTitle>{statusMessage}</DialogTitle>
      {currentStep === "DONE" && packageSize && (
        <DialogContentText>Approximate size: {packageSize / 1000}k</DialogContentText>
      )}
      {currentStep === "FAILURE" && (
        <DialogContentText>
          {packageStatus && parseFailureMessge(packageStatus?.info)}
        </DialogContentText>
      )}
    </StyledDialogContent>
  );
};

GeofencePackageProgress.propTypes = {
  handleClose: PropTypes.func.isRequired,
};

export default GeofencePackageProgress;
